import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Store, X, Sparkles, Loader, ClipboardList } from 'lucide-react';
import { EntityIcon } from '../../components/taskIcons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../packages/api-client';
import { createProject } from '../../packages/api-client/pm-api';
import { usePageTitle } from '../../hooks/usePageTitle';
import { PageHeader, StateView } from '../../packages/ui';

type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

interface TemplateTask {
  title: string;
  description?: string;
  priority?: TaskPriority;
}

interface MarketplaceTemplate {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  icon: string;
  task_count: number;
  is_public: boolean;
  tasks?: TemplateTask[];
}

const SUBTITLE = 'Built-in templates: each one starts a new project with its task list.';

const CATEGORIES = ['All', 'Software', 'Marketing', 'Operations', 'HR', 'Finance', 'Design', 'General'];

const SOFTWARE_SPRINT_TASKS: TemplateTask[] = [
  { title: 'Groom backlog and prioritise sprint candidates', priority: 'high' },
  { title: 'Hold sprint planning meeting', priority: 'high' },
  { title: 'Break down stories into technical tasks' },
  { title: 'Set up sprint branch and CI pipeline' },
  { title: 'Daily standup — day 1' },
  { title: 'Daily standup — mid-sprint' },
  { title: 'Mid-sprint demo / check-in' },
  { title: 'Code review open PRs', priority: 'high' },
  { title: 'QA regression pass' },
  { title: 'Sprint review with stakeholders' },
  { title: 'Sprint retrospective' },
  { title: 'Update release notes and deploy' },
];

const PRODUCT_LAUNCH_TASKS: TemplateTask[] = [
  { title: 'Finalise product positioning and messaging', priority: 'high' },
  { title: 'Write press release and media kit' },
  { title: 'Design landing page and launch assets' },
  { title: 'Brief sales and customer success teams' },
  { title: 'Prepare launch-day email campaign' },
  { title: 'Schedule social media announcements' },
  { title: 'Publish blog post and thought-leadership pieces' },
  { title: 'Coordinate analyst and influencer briefings' },
  { title: 'Run launch-day readiness check', priority: 'high' },
  { title: 'Monitor launch metrics and respond to issues', priority: 'urgent' },
  { title: 'Collect post-launch customer feedback' },
  { title: 'Post-launch retrospective and learnings' },
];

const ONBOARDING_TASKS: TemplateTask[] = [
  { title: 'Provision laptop, accounts and access', priority: 'high' },
  { title: 'Send welcome email with day-one agenda' },
  { title: 'Schedule orientation with HR' },
  { title: 'Assign onboarding buddy' },
  { title: 'Review role expectations and goals' },
  { title: 'Complete compliance and security training' },
  { title: '30-day check-in with manager' },
  { title: '60-day goals review' },
  { title: '90-day performance conversation' },
];

const MARKETING_CAMPAIGN_TASKS: TemplateTask[] = [
  { title: 'Define campaign objectives and KPIs', priority: 'high' },
  { title: 'Identify target audience and segments' },
  { title: 'Develop creative brief and messaging' },
  { title: 'Produce campaign assets (copy, design, video)' },
  { title: 'Plan paid media buy and channel mix' },
  { title: 'Set up tracking, UTMs and analytics' },
  { title: 'Launch campaign across channels', priority: 'high' },
  { title: 'Mid-campaign performance review' },
  { title: 'Optimise underperforming channels' },
  { title: 'Wrap-up report with ROI and learnings' },
];

const MOBILE_APP_TASKS: TemplateTask[] = [
  { title: 'Conduct user research and define personas', priority: 'high' },
  { title: 'Map user journeys and core flows' },
  { title: 'Create low-fidelity wireframes' },
  { title: 'Design high-fidelity UI and prototype' },
  { title: 'Set up mobile project scaffolding' },
  { title: 'Implement authentication and account flows' },
  { title: 'Build core feature screens' },
  { title: 'Integrate backend APIs' },
  { title: 'Add analytics and crash reporting' },
  { title: 'QA on iOS and Android devices', priority: 'high' },
  { title: 'Prepare App Store and Play Store listings' },
  { title: 'Submit for review and respond to feedback', priority: 'urgent' },
];

const WEBSITE_REDESIGN_TASKS: TemplateTask[] = [
  { title: 'Stakeholder discovery workshops' },
  { title: 'Audit existing site content and analytics' },
  { title: 'Conduct UX research and user interviews' },
  { title: 'Produce information architecture and sitemap' },
  { title: 'Define design system and component library' },
  { title: 'Design key page templates' },
  { title: 'Build frontend components' },
  { title: 'Migrate and author new content' },
  { title: 'Cross-browser and accessibility QA', priority: 'high' },
  { title: 'Stage launch dry-run' },
  { title: 'Go-live and DNS cutover', priority: 'urgent' },
];

const QBR_TASKS: TemplateTask[] = [
  { title: 'Pull financial results and variance analysis', priority: 'high' },
  { title: 'Compile KPI dashboard for each department' },
  { title: 'Collect departmental progress reports' },
  { title: 'Identify wins, risks and roadblocks' },
  { title: 'Draft executive summary and narrative' },
  { title: 'Build presentation deck' },
  { title: 'Run dry-run with leadership team' },
  { title: 'Present QBR to stakeholders', priority: 'high' },
  { title: 'Circulate recording, deck and action items' },
];

const OFFICE_RELOCATION_TASKS: TemplateTask[] = [
  { title: 'Define space requirements and budget', priority: 'high' },
  { title: 'Shortlist and tour candidate sites' },
  { title: 'Negotiate lease and sign agreement', priority: 'high' },
  { title: 'Plan floor layout and seating' },
  { title: 'Select moving company and schedule date' },
  { title: 'Coordinate IT network and hardware migration', priority: 'high' },
  { title: 'Update address with vendors, clients and registries' },
  { title: 'Communicate timeline to employees' },
  { title: 'Organise packing and labelling' },
  { title: 'Move day — physical relocation', priority: 'urgent' },
  { title: 'Unpack, test systems and resolve issues' },
  { title: 'Post-move retrospective' },
];

const RESEARCH_TASKS: TemplateTask[] = [
  { title: 'Define research question and scope', priority: 'high' },
  { title: 'Conduct literature review' },
  { title: 'Design methodology and protocol' },
  { title: 'Obtain approvals / IRB if needed' },
  { title: 'Collect data' },
  { title: 'Clean and analyse data' },
  { title: 'Draft findings and discussion' },
  { title: 'Peer review and revisions' },
  { title: 'Finalise write-up and publish' },
];

const CLIENT_DELIVERY_TASKS: TemplateTask[] = [
  { title: 'Kickoff meeting and goal alignment', priority: 'high' },
  { title: 'Discovery and requirements gathering' },
  { title: 'Draft scope of work and timeline' },
  { title: 'Client sign-off on scope', priority: 'high' },
  { title: 'Design / build phase' },
  { title: 'Internal QA review' },
  { title: 'Client review and feedback round' },
  { title: 'Apply revisions' },
  { title: 'Final delivery and handoff', priority: 'high' },
  { title: 'Retrospective and case study write-up' },
];

const BUILT_IN_TEMPLATES: MarketplaceTemplate[] = [
  {
    id: 'builtin-1', name: 'Software Sprint', slug: 'software-sprint', category: 'Software',
    description: 'A 2-week agile sprint with backlog grooming, daily standups, and retrospective tasks.',
    icon: 'wrench', task_count: SOFTWARE_SPRINT_TASKS.length, is_public: true, tasks: SOFTWARE_SPRINT_TASKS,
  },
  {
    id: 'builtin-2', name: 'Product Launch', slug: 'product-launch', category: 'Marketing',
    description: 'End-to-end product launch from positioning and press kit to go-live and post-launch review.',
    icon: 'rocket', task_count: PRODUCT_LAUNCH_TASKS.length, is_public: true, tasks: PRODUCT_LAUNCH_TASKS,
  },
  {
    id: 'builtin-3', name: 'Onboarding Plan', slug: 'onboarding-plan', category: 'HR',
    description: 'Employee onboarding checklist covering IT setup, culture orientation, and 30-60-90 day goals.',
    icon: 'check', task_count: ONBOARDING_TASKS.length, is_public: true, tasks: ONBOARDING_TASKS,
  },
  {
    id: 'builtin-4', name: 'Marketing Campaign', slug: 'marketing-campaign', category: 'Marketing',
    description: 'Multi-channel marketing campaign: strategy, content creation, distribution, and analytics.',
    icon: 'target', task_count: MARKETING_CAMPAIGN_TASKS.length, is_public: true, tasks: MARKETING_CAMPAIGN_TASKS,
  },
  {
    id: 'builtin-5', name: 'Mobile App Development', slug: 'mobile-app-dev', category: 'Software',
    description: 'Full-cycle mobile app from user research and wire-framing through development to app store submission.',
    icon: 'smartphone', task_count: MOBILE_APP_TASKS.length, is_public: true, tasks: MOBILE_APP_TASKS,
  },
  {
    id: 'builtin-6', name: 'Website Redesign', slug: 'website-redesign', category: 'Design',
    description: 'Complete website redesign with discovery, UX research, design system, development, and QA.',
    icon: 'palette', task_count: WEBSITE_REDESIGN_TASKS.length, is_public: true, tasks: WEBSITE_REDESIGN_TASKS,
  },
  {
    id: 'builtin-7', name: 'Quarterly Business Review', slug: 'qbr', category: 'Finance',
    description: 'QBR preparation: financial analysis, KPI review, department reports, and presentation build.',
    icon: 'chart', task_count: QBR_TASKS.length, is_public: true, tasks: QBR_TASKS,
  },
  {
    id: 'builtin-8', name: 'Office Relocation', slug: 'office-relocation', category: 'Operations',
    description: 'Office move plan from vendor selection and IT migration to employee communication and move day.',
    icon: 'building', task_count: OFFICE_RELOCATION_TASKS.length, is_public: true, tasks: OFFICE_RELOCATION_TASKS,
  },
  {
    id: 'builtin-9', name: 'Research Project', slug: 'research-project', category: 'General',
    description: 'Academic or professional research workflow: literature review, methodology, data collection, and write-up.',
    icon: 'flask', task_count: RESEARCH_TASKS.length, is_public: true, tasks: RESEARCH_TASKS,
  },
  {
    id: 'builtin-10', name: 'Client Delivery', slug: 'client-delivery', category: 'General',
    description: 'Agency client project delivery from kickoff and discovery through development, review, and handoff.',
    icon: 'check', task_count: CLIENT_DELIVERY_TASKS.length, is_public: true, tasks: CLIENT_DELIVERY_TASKS,
  },
];

interface TemplateMarketplaceProps {
  onClose?: () => void;
}

export function TemplateMarketplacePage({ onClose }: TemplateMarketplaceProps) {
  usePageTitle('Template Marketplace');
  const navigate = useNavigate();
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<MarketplaceTemplate | null>(null);

  // The catalogue is the built-in set shipped with the app. There is no
  // server catalogue (ledger drift D3: /marketplace/templates does not exist).
  const filtered = BUILT_IN_TEMPLATES.filter(t => {
    const matchCat = category === 'All' || t.category === category;
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const applyTemplate = async (template: MarketplaceTemplate) => {
    setCreating(template.id);
    try {
      const { id: projectId } = await createProject({
        name: template.name,
        description: template.description,
        icon: template.icon,
        color: '#1A9E8F',
      });

      if (template.tasks && template.tasks.length > 0) {
        for (const t of template.tasks) {
          await apiClient.post(`/projects/${projectId}/tasks`, {
            title: t.title,
            description: t.description ?? '',
            status: 'todo',
            priority: t.priority ?? 'medium',
          });
        }
      }

      navigate(`/projects/${projectId}`);
      onClose?.();
    } catch (e) {
      console.error('Failed to create project from template', e);
      toast.error('Failed to create the project from this template. Check Projects before retrying: part of it may have been created.');
    } finally {
      setCreating(null);
    }
  };

  return (
    <div className={onClose ? 'modal-overlay' : 'page-container'} onClick={e => onClose && e.target === e.currentTarget && onClose()}>
      <div className={onClose ? 'modal marketplace-modal' : 'marketplace-page'}>
        {/* Header */}
        {onClose ? (
          <div className="modal-header">
            <div>
              <h2 className="marketplace-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}><Store size={20} aria-hidden="true" /> Template Marketplace</h2>
              <p className="page-subtitle">{SUBTITLE}</p>
            </div>
            <button className="modal-close" onClick={onClose} aria-label="Close"><X size={18} aria-hidden="true" /></button>
          </div>
        ) : (
          <PageHeader title="Template Marketplace" subtitle={SUBTITLE} />
        )}

        {/* Search + category filter */}
        <div className="marketplace-filters">
          <input
            className="input"
            placeholder="Search templates…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: '320px' }}
          />
          <div className="marketplace-cats">
            {CATEGORIES.map(c => (
              <button
                key={c}
                className={`marketplace-cat${category === c ? ' active' : ''}`}
                onClick={() => setCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Preview panel */}
        {previewTemplate && (
          <div className="marketplace-preview">
            <div className="marketplace-preview-header">
              <div>
                <div className="marketplace-preview-title">
                  <span className="marketplace-preview-icon"><EntityIcon icon={previewTemplate.icon} size={24} /></span>
                  {previewTemplate.name}
                </div>
                <div className="marketplace-preview-meta">
                  {previewTemplate.category} · {previewTemplate.task_count} tasks
                </div>
              </div>
              <div className="marketplace-preview-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => applyTemplate(previewTemplate)}
                  disabled={creating === previewTemplate.id}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}
                >
                  {creating === previewTemplate.id ? <><Loader size={16} aria-hidden="true" /> Creating…</> : <><Sparkles size={16} aria-hidden="true" /> Use Template</>}
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => setPreviewTemplate(null)}
                  aria-label="Close preview"
                >
                  <X size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
            <p className="marketplace-preview-desc">{previewTemplate.description}</p>
            {previewTemplate.tasks && previewTemplate.tasks.length > 0 ? (
              <ol className="marketplace-preview-tasks">
                {previewTemplate.tasks.map((t, i) => (
                  <li key={i} className="marketplace-preview-task">
                    <span className="marketplace-preview-task-title">{t.title}</span>
                    {t.priority && t.priority !== 'medium' && (
                      <span className={`marketplace-preview-priority priority-${t.priority}`}>
                        {t.priority}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="marketplace-preview-empty">
                This template's task list isn't available for preview. Use the template to create the project with its tasks.
              </p>
            )}
          </div>
        )}

        {/* Template grid */}
        <div className="marketplace-grid">
          {filtered.map(template => (
            <div
              key={template.id}
              className={`marketplace-card${previewTemplate?.id === template.id ? ' previewing' : ''}`}
              onClick={() => setPreviewTemplate(prev => prev?.id === template.id ? null : template)}
            >
              <div className="marketplace-card-icon"><EntityIcon icon={template.icon} size={28} /></div>
              <div className="marketplace-card-body">
                <div className="marketplace-card-name">{template.name}</div>
                <div className="marketplace-card-cat">{template.category}</div>
                <div className="marketplace-card-desc">{template.description}</div>
              </div>
              <div className="marketplace-card-footer">
                <span className="marketplace-task-count" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}><ClipboardList size={14} aria-hidden="true" /> {template.task_count} tasks</span>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={e => { e.stopPropagation(); applyTemplate(template); }}
                  disabled={creating === template.id}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}
                >
                  {creating === template.id ? <><Loader size={14} aria-hidden="true" /> Creating…</> : <><Sparkles size={14} aria-hidden="true" /> Use Template</>}
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <StateView state="filtered-empty" message={`No built-in template matches${search ? ` "${search}"` : ''} in ${category}.`}
                onClearFilters={() => { setSearch(''); setCategory('All'); }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
